import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  QrCode, Barcode, ScanLine, FileImage, FileCode, BookOpen, Info,
  Tags, History, X, ArrowRight, Mail, Search, Clock, Star, Trash2, GripVertical, Download, Sparkles, Heart,
} from 'lucide-react';
import { useLang } from '../i18n';
import { trackBookClick } from '../bookData';

const RECENT_KEY = 'bsd_recent';
const RECENT_MAX = 4;
const FAV_KEY = 'bsd_favs';
const FAV_MAX = 12;

const GEN_ITEMS = [
  ['qr-code-generator', 'QR-Code', QrCode],
  ['barcode-generator', 'Barcode', Barcode],
  ['ean-13-generator', 'EAN-13', Barcode],
  ['ean-8-generator', 'EAN-8', Barcode],
  ['upc-generator', 'UPC-A', Barcode],
  ['code-128-generator', 'Code 128', Barcode],
  ['code-39-generator', 'Code 39', Barcode],
  ['isbn-barcode-generator', 'ISBN', Barcode],
  ['gs1-barcode-generator', 'GS1-128', Barcode],
];

const CONV_ITEMS = [
  ['image-to-barcode', ScanLine, { de: 'Bild → Barcode', en: 'Image → Barcode' }],
  ['barcode-to-image', Barcode, { de: 'Barcode → Bild', en: 'Barcode → Image' }],
  ['svg-to-png', FileImage, { de: 'SVG → PNG', en: 'SVG → PNG' }],
  ['png-to-svg', FileCode, { de: 'PNG → SVG', en: 'PNG → SVG' }],
];

const readList = (k) => {
  try { const raw = localStorage.getItem(k); return raw ? (JSON.parse(raw) || []) : []; } catch (e) { return []; }
};
const readRecent = () => readList(RECENT_KEY);
const recordRecent = (h, l) => {
  try {
    let arr = readRecent().filter((x) => x && x.h !== h);
    arr.unshift({ h, l });
    arr = arr.slice(0, RECENT_MAX);
    localStorage.setItem(RECENT_KEY, JSON.stringify(arr));
  } catch (e) { console.error('SideNav: non-critical error:', e); }
};

export const SideNav = ({ open, onClose, onOpen, onOpenContact, onOpenConverter, onGoTab, onScrollToGenerators, activeTab, converterMode }) => {
  const { lang } = useLang();
  const de = lang === 'de';
  const base = de ? '' : '/en';
  const [query, setQuery] = useState('');
  const [recent, setRecent] = useState([]);
  const [favs, setFavs] = useState([]);
  const [kbIndex, setKbIndex] = useState(-1);
  const searchRef = useRef(null);
  const rootRef = useRef(null);
  const overlayRef = useRef(null);
  const asideRef = useRef(null);
  const itemRefs = useRef([]);
  const dragIndex = useRef(null);
  const favListRef = useRef(null);
  const favDragRef = useRef(false);
  const touchDragIdx = useRef(null);
  const [dragFav, setDragFav] = useState(null);

  const path = typeof window !== 'undefined' ? window.location.pathname.replace(/\/+$/, '') : '';
  const isActivePath = (href) => {
    const h = (href || '').replace(/\/+$/, '');
    return h && (path === h);
  };

  // Close on ESC + lock body scroll + focus search + refresh recent list.
  useEffect(() => {
    if (!open) return undefined;
    setRecent(readRecent());
    setFavs(readList(FAV_KEY));
    const onEsc = (e) => { if (e.key === 'Escape') onClose && onClose(); };
    document.addEventListener('keydown', onEsc);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const id = setTimeout(() => { try { searchRef.current && searchRef.current.focus(); } catch (e) { console.error('SideNav: non-critical error:', e); } }, 60);
    return () => {
      document.removeEventListener('keydown', onEsc);
      document.body.style.overflow = prev;
      clearTimeout(id);
    };
  }, [open, onClose]);

  useEffect(() => { if (!open) { setQuery(''); setKbIndex(-1); } }, [open]);

  // Live swipe: the drawer follows the finger while dragging (open from the
  // left edge, close by dragging left) and snaps at ~50% on release.
  useEffect(() => {
    const aside = asideRef.current;
    const overlay = overlayRef.current;
    const root = rootRef.current;
    if (!aside) return undefined;
    let sx = 0; let sy = 0; let dragging = false; let activated = false; let w = 288; let opening = false;

    const onStart = (e) => {
      if (e.touches.length !== 1 || favDragRef.current) return;
      const t = e.touches[0];
      sx = t.clientX; sy = t.clientY;
      w = aside.offsetWidth || 288;
      if (open) { dragging = true; opening = false; }
      else if (sx <= 24) { dragging = true; opening = true; }
      else { dragging = false; }
      activated = false;
    };
    const onMove = (e) => {
      if (!dragging || favDragRef.current) return;
      const t = e.touches[0];
      const dx = t.clientX - sx; const dy = t.clientY - sy;
      if (!activated) {
        if (Math.abs(dx) < 8 || Math.abs(dx) <= Math.abs(dy)) { if (Math.abs(dy) > 12) dragging = false; return; }
        if (opening && dx < 0) { dragging = false; return; }
        if (!opening && dx > 0) { dragging = false; return; }
        activated = true;
        aside.style.transition = 'none';
        if (overlay) overlay.style.transition = 'none';
        if (root) root.style.pointerEvents = 'auto';
      }
      e.preventDefault();
      const x = opening ? Math.min(0, -w + dx) : Math.max(-w, dx);
      aside.style.transform = `translateX(${x}px)`;
      if (overlay) overlay.style.opacity = String(Math.max(0, Math.min(1, (x + w) / w)));
    };
    const onEnd = () => {
      if (!dragging) return;
      const wasActivated = activated;
      dragging = false; activated = false;
      if (!wasActivated) return;
      const m = /translateX\((-?[0-9.]+)px\)/.exec(aside.style.transform || '');
      const x = m ? parseFloat(m[1]) : (opening ? -w : 0);
      aside.style.transition = '';
      aside.style.transform = '';
      if (overlay) { overlay.style.transition = ''; overlay.style.opacity = ''; }
      if (root) root.style.pointerEvents = '';
      if (x > -w / 2) { if (!open) onOpen && onOpen(); } else if (open) { onClose && onClose(); }
    };

    window.addEventListener('touchstart', onStart, { passive: true });
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onEnd, { passive: true });
    window.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      window.removeEventListener('touchstart', onStart);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
      window.removeEventListener('touchcancel', onEnd);
    };
  }, [open, onOpen, onClose]);

  // Keyboard shortcut: "/" opens the menu and focuses the search field.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target;
      const tag = t && t.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || (t && t.isContentEditable)) return;
      e.preventDefault();
      if (!open) { onOpen && onOpen(); } else { try { searchRef.current && searchRef.current.focus(); } catch (er) { console.error('SideNav: non-critical error:', er); } }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onOpen]);

  // Touch reorder: long-press a favourite, then drag to reorder (mobile).
  useEffect(() => {
    const el = favListRef.current;
    if (!el) return undefined;
    let lp = null; let startX = 0; let startY = 0; let startIdx = null; let active = false;
    const cancelLp = () => { if (lp) { clearTimeout(lp); lp = null; } };
    const onStart = (e) => {
      if (e.touches.length !== 1) return;
      const row = e.target.closest && e.target.closest('[data-favindex]');
      if (!row) return;
      const t = e.touches[0];
      startX = t.clientX; startY = t.clientY;
      startIdx = parseInt(row.getAttribute('data-favindex'), 10);
      active = false;
      cancelLp();
      lp = setTimeout(() => {
        active = true; favDragRef.current = true; touchDragIdx.current = startIdx;
        setDragFav(startIdx);
        try { if (navigator.vibrate) navigator.vibrate(15); } catch (er) { console.error('SideNav: non-critical error:', er); }
      }, 300);
    };
    const onMove = (e) => {
      const t = e.touches[0];
      if (!active) {
        if (Math.abs(t.clientX - startX) > 10 || Math.abs(t.clientY - startY) > 10) cancelLp();
        return;
      }
      e.preventDefault();
      const under = document.elementFromPoint(t.clientX, t.clientY);
      const row = under && under.closest ? under.closest('[data-favindex]') : null;
      if (!row) return;
      const to = parseInt(row.getAttribute('data-favindex'), 10);
      const from = touchDragIdx.current;
      if (isNaN(to) || from == null || from === to) return;
      reorderFav(from, to);
      touchDragIdx.current = to;
      setDragFav(to);
    };
    const onEnd = () => {
      cancelLp();
      active = false; favDragRef.current = false; touchDragIdx.current = null;
      setDragFav(null);
    };
    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd, { passive: true });
    el.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      cancelLp();
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
    // eslint-disable-next-line
  }, [open, favs.length]);

  const close = () => onClose && onClose();
  const goTab = (t) => { if (onGoTab) onGoTab(t); };
  const goConverter = (m) => { if (onOpenConverter) onOpenConverter(m); };
  const goGenerators = () => { if (onScrollToGenerators) onScrollToGenerators(); };
  const openContact = () => { if (onOpenContact) onOpenContact(); };
  const goHref = (href, label) => { recordRecent(href, label); window.location.href = href; };

  const favSet = useMemo(() => new Set((favs || []).map((f) => (f.h || '').replace(/\/+$/, ''))), [favs]);
  const isFav = (href) => favSet.has((href || '').replace(/\/+$/, ''));
  const toggleFav = (href, label) => {
    setFavs((prev) => {
      const key = (href || '').replace(/\/+$/, '');
      const exists = prev.some((x) => (x.h || '').replace(/\/+$/, '') === key);
      const next = exists ? prev.filter((x) => (x.h || '').replace(/\/+$/, '') !== key) : [{ h: href, l: label }, ...prev].slice(0, FAV_MAX);
      try { localStorage.setItem(FAV_KEY, JSON.stringify(next)); } catch (e) { console.error('SideNav: non-critical error:', e); }
      return next;
    });
  };
  const clearRecent = () => { try { localStorage.removeItem(RECENT_KEY); } catch (e) { console.error('SideNav: non-critical error:', e); } setRecent([]); };
  const reorderFav = (from, to) => {
    setFavs((prev) => {
      if (from == null || to == null || from < 0 || to < 0 || from >= prev.length || to >= prev.length) return prev;
      const arr = [...prev];
      const [m] = arr.splice(from, 1);
      arr.splice(to, 0, m);
      try { localStorage.setItem(FAV_KEY, JSON.stringify(arr)); } catch (e) { console.error('SideNav: non-critical error:', e); }
      return arr;
    });
  };
  const exportFavs = () => {
    try {
      const data = JSON.stringify({ favorites: favs }, null, 2);
      const blob = new Blob([data], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'barcode-favoriten.json';
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { console.error('SideNav: non-critical error:', e); }
  };

  const q = query.trim().toLowerCase();
  const match = (label) => !q || label.toLowerCase().includes(q);

  const gens = useMemo(() => GEN_ITEMS.filter(([, label]) => match(label)), [q]);
  const convs = useMemo(() => CONV_ITEMS.filter(([, , lbl]) => match(de ? lbl.de : lbl.en)), [q, de]);
  const recentFiltered = useMemo(() => (q ? [] : recent).filter((r) => r && r.l), [q, recent]);
  const favsFiltered = useMemo(() => (favs || []).filter((f) => f && f.l && match(f.l)), [q, favs]);

  const toolItems = [
    ['labels', de ? 'A4-Etiketten' : 'A4 labels', Tags, 'tab'],
    ['history', de ? 'Verlauf' : 'History', History, 'tab'],
    ['guides', de ? 'Ratgeber' : 'Guides', BookOpen, 'link'],
  ];
  const tools = toolItems.filter(([, label]) => match(label));
  const showAbout = match(de ? 'Über uns' : 'About');
  const showContact = match(de ? 'Kontakt' : 'Contact');
  const showBook = match(de ? 'Buchtipp Die Wolkenpost von Funkelmoor' : 'Book tip Die Wolkenpost von Funkelmoor');
  const showSupport = match(de ? 'Unterstützen Spenden Support' : 'Support donate Unterstützen');

  // Build a flat, ordered list of actionable rows for keyboard navigation.
  const groups = [];
  if (favsFiltered.length) {
    groups.push({
      title: de ? 'Favoriten' : 'Favorites',
      sortable: !q,
      note: true,
      items: favsFiltered.map((f, i) => ({ key: `fav-${i}`, label: f.l, Icon: Star, href: f.h, active: isActivePath(f.h), pinnable: true, favIndex: i, testid: `sidenav-fav-${i}`, activate: () => goHref(f.h, f.l) })),
    });
  }
  if (recentFiltered.length) {
    groups.push({
      title: de ? 'Zuletzt genutzt' : 'Recently used',
      action: { onClick: clearRecent, label: de ? 'Verlauf löschen' : 'Clear history', testid: 'sidenav-clear-recent' },
      items: recentFiltered.map((r, i) => ({ key: `rec-${i}`, label: r.l, Icon: Clock, href: r.h, active: isActivePath(r.h), pinnable: true, testid: `sidenav-recent-${i}`, activate: () => goHref(r.h, r.l) })),
    });
  }
  if (gens.length) {
    const items = gens.map(([slug, label, Icon]) => ({ key: `gen-${slug}`, label, Icon, href: `${base}/${slug}/`, active: isActivePath(`${base}/${slug}/`), pinnable: true, testid: `sidenav-gen-${slug}`, activate: () => goHref(`${base}/${slug}/`, label) }));
    if (!q) items.push({ key: 'gen-more', label: de ? 'Alle Generatoren' : 'All generators', Icon: ArrowRight, accent: true, testid: 'sidenav-gen-more', activate: goGenerators });
    groups.push({ title: 'Generator', items });
  }
  if (convs.length) {
    groups.push({ title: de ? 'Konverter' : 'Converter', items: convs.map(([m, Icon, lbl]) => ({ key: `conv-${m}`, label: de ? lbl.de : lbl.en, Icon, active: activeTab === 'convert' && converterMode === m, testid: `sidenav-conv-${m}`, activate: () => goConverter(m) })) });
  }
  if (tools.length) {
    groups.push({ title: 'Tools', items: tools.map(([key, label, Icon, kind]) => (
      kind === 'link'
        ? { key: `tool-${key}`, label, Icon, href: de ? '/ratgeber/' : '/en/guides/', testid: 'sidenav-tool-guides', activate: () => { window.location.href = de ? '/ratgeber/' : '/en/guides/'; } }
        : { key: `tool-${key}`, label, Icon, active: activeTab === key, testid: `sidenav-tool-${key}`, activate: () => goTab(key) }
    )) });
  }
  const footItems = [];
  if (showSupport) footItems.push({ key: 'supporter', label: de ? 'Unterstützen' : 'Support us', Icon: Heart, iconAccent: true, href: '/supporter', testid: 'sidenav-supporter', activate: () => { window.location.href = '/supporter'; } });
  if (showBook) footItems.push({ key: 'book', label: de ? 'Buchtipp: Funkelmoor' : 'Book tip: Funkelmoor', Icon: Sparkles, iconAccent: true, href: '/?buch=1', testid: 'sidenav-book', activate: () => { trackBookClick('menu_link'); window.location.href = '/?buch=1'; } });
  if (showAbout) footItems.push({ key: 'about', label: de ? 'Über uns' : 'About', Icon: Info, iconAccent: true, href: `${base}/about/`, testid: 'sidenav-about', activate: () => { window.location.href = `${base}/about/`; } });
  if (showContact) footItems.push({ key: 'contact', label: de ? 'Kontakt' : 'Contact', Icon: Mail, iconAccent: true, testid: 'sidenav-contact', activate: openContact });
  if (footItems.length) groups.push({ divider: true, items: footItems });

  const flat = groups.flatMap((g) => g.items);
  flat.forEach((it, i) => { it.index = i; });
  const noResults = flat.length === 0;

  // Reset / clamp keyboard highlight when the result set changes.
  useEffect(() => { setKbIndex(q && flat.length ? 0 : -1); /* eslint-disable-next-line */ }, [q, recent.length, favs.length, activeTab, converterMode]);
  useEffect(() => {
    const el = itemRefs.current[kbIndex];
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [kbIndex]);

  const onSearchKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setKbIndex((i) => Math.min(flat.length - 1, (i < 0 ? -1 : i) + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setKbIndex((i) => Math.max(0, (i < 0 ? 0 : i) - 1)); }
    else if (e.key === 'Enter') { if (kbIndex >= 0 && flat[kbIndex]) { e.preventDefault(); flat[kbIndex].activate(); } }
  };

  const rowCls = 'flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-sm text-foreground/90 transition-colors hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
  const activeRow = 'bg-primary/10 font-medium text-primary';
  const kbRow = 'bg-secondary ring-2 ring-inset ring-primary/40';
  const sectionCls = 'px-2.5 pb-1 pt-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground';

  const renderItem = (it) => {
    const cls = `${rowCls} ${it.pinnable ? 'pr-9' : ''} ${it.accent ? 'font-medium text-primary' : ''} ${it.active ? activeRow : ''} ${kbIndex === it.index ? kbRow : ''}`;
    const pinned = it.pinnable && isFav(it.href);
    const icon = <it.Icon className={`h-4 w-4 shrink-0 ${it.iconAccent || it.accent ? 'text-primary' : (pinned && it.Icon === Star ? 'fill-primary text-primary' : 'text-muted-foreground')}`} />;
    const ref = (el) => { itemRefs.current[it.index] = el; };
    const tracksRecent = it.key.startsWith('gen-') || it.key.startsWith('rec-') || it.key.startsWith('fav-');
    const clickable = it.href ? (
      <a key="c" ref={ref} href={it.href} onClick={() => { if (tracksRecent) recordRecent(it.href, it.label); }} aria-current={it.active ? 'page' : undefined} className={cls} data-testid={it.testid}>
        {icon}{it.label}
      </a>
    ) : (
      <button key="c" ref={ref} type="button" onClick={it.activate} aria-current={it.active ? 'true' : undefined} className={cls} data-testid={it.testid}>
        {icon}{it.label}
      </button>
    );
    if (!it.pinnable) return React.cloneElement(clickable, { key: it.key });
    return (
      <div key={it.key} className="relative">
        {clickable}
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFav(it.href, it.label); }}
          aria-label={pinned ? (de ? 'Favorit entfernen' : 'Unpin favorite') : (de ? 'Als Favorit anpinnen' : 'Pin as favorite')}
          aria-pressed={pinned}
          data-testid={`sidenav-pin-${it.key}`}
          className="absolute right-1 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Star className={`h-4 w-4 ${pinned ? 'fill-primary text-primary' : ''}`} />
        </button>
      </div>
    );
  };

  const favLinkCls = 'flex flex-1 items-center gap-2.5 rounded-md py-2 pl-1 pr-9 text-sm text-foreground/90 transition-colors hover:text-foreground focus-visible:outline-none';
  const renderFavRow = (it, sortable) => (
    <div
      key={it.key}
      data-favindex={it.favIndex}
      draggable={sortable}
      onDragStart={sortable ? (e) => { dragIndex.current = it.favIndex; e.dataTransfer.effectAllowed = 'move'; } : undefined}
      onDragOver={sortable ? (e) => { e.preventDefault(); const from = dragIndex.current; const to = it.favIndex; if (from == null || from === to) return; reorderFav(from, to); dragIndex.current = to; } : undefined}
      onDragEnd={sortable ? () => { dragIndex.current = null; } : undefined}
      className={`group relative flex select-none items-center rounded-md transition-[opacity,box-shadow] ${dragFav === it.favIndex ? 'bg-secondary opacity-70 shadow-md ring-2 ring-primary/50' : (it.active ? activeRow : '')} ${kbIndex === it.index ? kbRow : ''} hover:bg-secondary`}
      data-testid={`sidenav-favrow-${it.favIndex}`}
    >
      {sortable && (
        <span className="flex cursor-grab items-center pl-1 text-muted-foreground active:cursor-grabbing" aria-hidden="true" title={de ? 'Halten & ziehen zum Sortieren' : 'Hold & drag to reorder'}>
          <GripVertical className="h-4 w-4" />
        </span>
      )}
      <a
        ref={(el) => { itemRefs.current[it.index] = el; }}
        href={it.href}
        onClick={() => recordRecent(it.href, it.label)}
        aria-current={it.active ? 'page' : undefined}
        className={`${favLinkCls} ${sortable ? '' : 'pl-2.5'}`}
        data-testid={it.testid}
      >
        <Star className="h-4 w-4 shrink-0 fill-primary text-primary" />{it.label}
      </a>
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFav(it.href, it.label); }}
        aria-label={de ? 'Favorit entfernen' : 'Unpin favorite'}
        data-testid={`sidenav-unpin-${it.favIndex}`}
        className="absolute right-1 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-background hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Star className="h-4 w-4 fill-primary text-primary" />
      </button>
    </div>
  );

  return (
    <div
      ref={rootRef}
      className={`fixed inset-0 z-50 ${open ? '' : 'pointer-events-none'}`}
      aria-hidden={open ? undefined : true}
      data-testid="sidenav-root"
    >
      <div
        ref={overlayRef}
        className={`absolute inset-0 bg-black/40 transition-opacity duration-200 ${open ? 'opacity-100' : 'opacity-0'}`}
        onClick={close}
        data-testid="sidenav-backdrop"
      />

      <aside
        ref={asideRef}
        className={`absolute left-0 top-0 flex h-full w-72 max-w-[85%] flex-col overflow-hidden border-r bg-background shadow-xl transition-transform duration-200 ease-out ${open ? 'translate-x-0' : '-translate-x-full'}`}
        aria-label={de ? 'Navigation' : 'Navigation'}
        data-testid="sidenav-drawer"
      >
        <div className="flex items-center justify-between px-3 py-3">
          <span className="font-heading text-sm font-semibold text-foreground">{de ? 'Navigation' : 'Navigation'}</span>
          <button type="button" onClick={close} className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" aria-label={de ? 'Menü schließen' : 'Close menu'} data-testid="sidenav-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-3 pb-1">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              ref={searchRef}
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onSearchKeyDown}
              placeholder={de ? 'Suchen…' : 'Search…'}
              aria-label={de ? 'Menü durchsuchen' : 'Search menu'}
              data-testid="sidenav-search"
              className="w-full rounded-md border bg-secondary/40 py-2 pl-8 pr-3 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-1.5 pb-4" data-testid="sidenav">
          {groups.map((g, gi) => (
            <div key={g.title || `grp-${gi}`} className={g.divider ? 'mt-4 border-t pt-2' : ''}>
              {g.title && (
                g.action ? (
                  <div className="flex items-center justify-between pr-1.5">
                    <div className={sectionCls}>{g.title}</div>
                    <button type="button" onClick={g.action.onClick} aria-label={g.action.label} title={g.action.label} data-testid={g.action.testid} className="mt-2 rounded-md p-1 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : <div className={sectionCls}>{g.title}</div>
              )}
              {g.sortable
                ? <div ref={favListRef}>{g.items.map((it) => renderFavRow(it, true))}</div>
                : g.items.map(renderItem)}
              {g.note && (
                <div className="mt-1 flex items-center justify-between gap-2 px-2.5 py-1">
                  <span className="flex items-center gap-1 text-[11px] leading-tight text-muted-foreground">
                    <Info className="h-3 w-3 shrink-0" />{de ? 'Nur auf diesem Gerät' : 'This device only'}
                  </span>
                  <button type="button" onClick={exportFavs} data-testid="sidenav-export-favs" className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[11px] font-medium text-primary transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                    <Download className="h-3 w-3" />{de ? 'Exportieren' : 'Export'}
                  </button>
                </div>
              )}
            </div>
          ))}

          {noResults && (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground" data-testid="sidenav-empty">
              {de ? 'Keine Treffer' : 'No results'}
            </p>
          )}
        </nav>
      </aside>
    </div>
  );
};

export default SideNav;
